import {sweetAPI} from 'app/store/sweet-api';
import {
  convertLocationParamsToFetchParams,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {NOTIFICATIONS_API, SUBSCRIBERS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {NOTIFICATION_TAG} from 'app/utils/rtk-tags';

import type {
  NotificationsFetchParamsType,
  NotificationsLocationParamsType,
  NotificationType,
  PreviewEmailParamsType,
  PreviewEmailResponseType,
} from './types';
import type {SubscriptableType} from 'app/notifly/actions';
import type {BasicUserSerializedType, ListResponseType} from 'sweet-types';

export type SubscribersFetchParamsType = {
  object_id: number;
  page?: string | number;
  content_type: SubscriptableType;
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [NOTIFICATION_TAG]})
  .injectEndpoints({
    endpoints: (build) => ({
      notifications: build.query<
        ListResponseType<NotificationType>,
        NotificationsFetchParamsType
      >({
        query: (params) => ({
          url: NOTIFICATIONS_API,
          params,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not fetch notifications.'),
          })(args, api);
        },
        providesTags: providesTagsByURL(NOTIFICATION_TAG),
      }),
      subscribers: build.query<
        ListResponseType<BasicUserSerializedType>,
        SubscribersFetchParamsType
      >({
        query: (params) => ({
          url: SUBSCRIBERS_API_URL,
          params,
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () => gt('Could not load subscribers.'),
          })(args, api);
        },
      }),
      updateNotification: build.mutation<NotificationType, NotificationType>({
        query: (notification) => ({
          url: notification.url,
          method: 'PATCH',
          body: notification,
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () => gt('Could not update the notification.'),
          })(args, api);
        },
        invalidatesTags: (notification) => [
          {type: NOTIFICATION_TAG, id: 'list'},
          {type: NOTIFICATION_TAG, id: notification?.url ?? ''},
        ],
      }),
      previewEmail: build.mutation<
        PreviewEmailResponseType,
        PreviewEmailParamsType
      >({
        query: ({content_type, verb, dynamic_data}) => ({
          url: `${NOTIFICATIONS_API}preview_email/`,
          method: 'POST',
          body: {
            content_type: content_type,
            verb: verb,
            dynamic_data: dynamic_data,
          },
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () =>
              gt('Could not generate email preview at the moment.'),
          })(args, api);
        },
      }),
    }),
  });

export const {
  useNotificationsQuery: useNotifications,
  useSubscribersQuery: useSubscribers,
  useUpdateNotificationMutation: useUpdateNotification,
  usePreviewEmailMutation: usePreviewEmail,
} = extendedAPI;

export const convertNotificationLocationParamsToFetchParams = (
  params: NotificationsLocationParamsType
): NotificationsFetchParamsType => ({
  ...convertLocationParamsToFetchParams(params),
  web_visible: params.web_visible != null ? params.web_visible : 'true',
  is_all: params.is_all != null ? params.is_all : 'true',
});
